import { Link } from "react-router-dom";
import type { Shift } from "../../api/types";
import { localDayKey } from "../../utils/dayKey";
import { hhmm } from "../../utils/homeFormat";

/** The person's shifts on one day, earliest first (a split day is two rows). */
function shiftsOn(shifts: Shift[], dayKey: string): Shift[] {
  return shifts
    .filter((s) => s.status !== "CANCELED" && localDayKey(new Date(s.plannedStart)) === dayKey)
    .sort((a, b) => a.plannedStart.localeCompare(b.plannedStart));
}

/**
 * G-204 (owner 2026-11-04): a manager's or florist's home opens with their own
 * shift today — the hours in large type and whether they are clocked in — in
 * place of the old grey greeting line. No shop name: people work in one shop.
 */
export function YourShiftCard({ shifts, todayKey }: { shifts: Shift[] | null; todayKey: string }) {
  const today = shifts ? shiftsOn(shifts, todayKey) : null;
  const first = today?.[0] ?? null;
  const last = today?.[today.length - 1] ?? null;
  return (
    <div className="card an-card home-shift-card">
      <div className="home-shift-main">
        <span className="kit-stat-label">Your shift</span>
        {shifts == null ? (
          <strong className="home-shift-hours">…</strong>
        ) : first && last ? (
          <strong className="home-shift-hours">
            {hhmm(first.plannedStart)}–{hhmm(last.plannedEnd)}
          </strong>
        ) : (
          <strong className="home-shift-hours muted">No shift today</strong>
        )}
      </div>
      {first ? (
        first.actualEnd ? (
          <span className="badge muted">Done {hhmm(first.actualEnd)}</span>
        ) : first.actualStart ? (
          <span className="badge success">In since {hhmm(first.actualStart)}</span>
        ) : (
          <span className="badge warning">Not clocked in</span>
        )
      ) : null}
    </div>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Monday of the week holding `d`, local time. */
export function mondayOf(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7));
}

/**
 * G-204: the person's week, Monday to Sunday — hours or "off", today marked,
 * a past day "worked" once clocked out. Links to My shifts for the rest.
 */
export function YourWeekCard({
  shifts,
  todayKey,
  monday,
}: {
  shifts: Shift[] | null;
  todayKey: string;
  monday: Date;
}) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    return { label: DAY_LABELS[i], key: localDayKey(d) };
  });
  return (
    <div className="card an-card home-week-card">
      <div className="an-card-head">
        <strong>Your week</strong>
        <Link to="/app/my/shifts" className="button plain">
          My shifts
        </Link>
      </div>
      {shifts == null ? (
        <p className="muted an-note">Loading…</p>
      ) : (
        <ul className="home-week">
          {days.map((day) => {
            const rows = shiftsOn(shifts, day.key);
            const isToday = day.key === todayKey;
            const worked = rows.length > 0 && day.key < todayKey && rows.every((s) => s.actualEnd);
            return (
              <li key={day.key} className={isToday ? "is-today" : undefined}>
                <span className="home-week-day">{day.label}</span>
                {rows.length === 0 ? (
                  <span className="muted">off</span>
                ) : (
                  <span className="home-week-hours">
                    {rows.map((s) => `${hhmm(s.plannedStart)}–${hhmm(s.plannedEnd)}`).join(" and ")}
                  </span>
                )}
                {isToday ? (
                  <span className="badge success">today</span>
                ) : worked ? (
                  <span className="muted home-week-note">worked</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
